import { AGREEMENT_LIMITS } from 'models/Agreement';
import { getClamped } from 'numberUtils';

export type AgreementBox = {
  x: number;
  y: number;
  width: number;
  height: number;
};

export type AgreementPoint = { x: number; y: number };

export type AgreementPageSize = { width: number; height: number };

export const getPageNumber = (pageIndex: number) => pageIndex + 1;

export const getPageIndex = (pageNumber: number) => pageNumber - 1;

export const pointsToPixels = <T extends AgreementBox>(
  box: T,
  scale: number,
): T => ({
  ...box,
  x: box.x * scale,
  y: box.y * scale,
  width: box.width * scale,
  height: box.height * scale,
});

export const pixelsToPoints = <T extends AgreementBox>(
  box: T,
  scale: number,
): T => pointsToPixels(box, 1 / scale);

export const getPointInPoints = (
  clientX: number,
  clientY: number,
  pageRect: Pick<DOMRect, 'left' | 'top'>,
  scale: number,
): AgreementPoint => ({
  x: (clientX - pageRect.left) / scale,
  y: (clientY - pageRect.top) / scale,
});

export const getPageSizeInPoints = (
  renderedWidth: number,
  renderedHeight: number,
  scale: number,
): AgreementPageSize => ({
  width: renderedWidth / scale,
  height: renderedHeight / scale,
});

export const clampBoxToPage = <T extends AgreementBox>(
  box: T,
  pageSize: AgreementPageSize,
  minSize: number = AGREEMENT_LIMITS.MIN_FIELD_SIZE,
): T => {
  const width = getClamped(box.width, minSize, pageSize.width);
  const height = getClamped(box.height, minSize, pageSize.height);

  return {
    ...box,
    width,
    height,
    x: getClamped(box.x, 0, pageSize.width - width),
    y: getClamped(box.y, 0, pageSize.height - height),
  };
};

export const getBoxCenteredAt = (
  center: AgreementPoint,
  size: Pick<AgreementBox, 'width' | 'height'>,
  pageSize: AgreementPageSize,
): AgreementBox =>
  clampBoxToPage(
    {
      x: center.x - size.width / 2,
      y: center.y - size.height / 2,
      width: size.width,
      height: size.height,
    },
    pageSize,
  );

export const getMovedBox = <T extends AgreementBox>(
  box: T,
  deltaX: number,
  deltaY: number,
  pageSize: AgreementPageSize,
): T =>
  clampBoxToPage({ ...box, x: box.x + deltaX, y: box.y + deltaY }, pageSize);

export const getResizedBox = <T extends AgreementBox>(
  box: T,
  deltaWidth: number,
  deltaHeight: number,
  pageSize: AgreementPageSize,
  minSize: number = AGREEMENT_LIMITS.MIN_FIELD_SIZE,
): T => ({
  ...box,
  width: getClamped(box.width + deltaWidth, minSize, pageSize.width - box.x),
  height: getClamped(
    box.height + deltaHeight,
    minSize,
    pageSize.height - box.y,
  ),
});
